// src/pages/writing/loose-leaves/leave.js
import React from "react";
import withBlogSEO from "../../../utils/withBlogSEO";
import FootnoteLink, {
  FootnoteProvider,
} from "../../../components/FootnoteLink";

const A = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-600 no-underline hover:underline"
  >
    {children}
  </a>
);

function Leave() {
  return (
    <FootnoteProvider>
      <div className="max-w-2xl mx-auto px-4 py-8 prose prose-sm [&_a]:text-blue-600 [&_a]:no-underline [&_a:hover]:underline">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">please leave</h1>
        <div className="text-gray-600">
          <p className="italic text-sm mb-4">[3/2026]</p>

            <div className="whitespace-pre-line text-base">
              <blockquote className="mb-6 border-l-4 pl-4">
                <p className="italic">
                  They said they wanted simply to carve<br />
                  a 25 mile buffer zone and then<br />
                  they ravaged your<br />
                  water supplies your electricity your<br />
                  hospitals your schools your highways and byways all<br />
                  the way north to Beirut because they said this<br />
                  was their quest for peace<br />
                  They blew up your homes and demolished the grocery<br />
                  stores and blocked the Red Cross and took away doctors<br />
                  to jail and they cluster-bombed girls and boys<br />
                  whose bodies<br />
                  swelled purple and black into twice the original size<br />
                  and tore the buttocks from a four month old baby<br />
                  and then<br />
                  they said this was brilliant<br />
                  military accomplishment and this was done<br />
                  they said in the name of self-defense they said<br />
                  that is the noblest concept<br />
                  of mankind isn’t that obvious?
                </p>
                <cite className="block mt-2 text-sm not-italic text-gray-600">
                  &mdash; excerpt from “Apologies to All the People in Lebanon” by June Jordan, published in 2005{" "}
                </cite>
              </blockquote>
          </div>
        <p className="mb-4">The last two weeks have borne advanced variants of hell throughout the Middle East: bombed schools and ecocide in Iran, the forced displacement of more than 759,000 people in Lebanon, settler murders in the West Bank &mdash; alongside continued destruction, famine, and containment in Gaza. 
          {/* I live these days as I have all these days before, in disbelief that this reality is the one which managed to thread itself from the past, and in disbelief at the discord between what I observe and how my local America tries to force me to acquiesce to its crimes. I watch on, in spite of limited free press, in spite of journalists who are murdered for their reporting, in spite of threats of punishment for speaking out. In spite of mass surveillance, in spite of websites devoted to ruining protesters’ lives, in spite of living in a world with far too many people who are unable to exercise their ability to imagine a life outside of their own. */}
          </p>
        <p className="mb-4">
          I watch <A href = "https://x.com/never_oppressed/status/2030407931229823459?s=20">explosions</A> <A href="https://x.com/AssalRad/status/2030432926329114882?s=20">rain hell</A> from the sky in Iran, and <A href="https://x.com/jleonros/status/2030544811212566625?s=20">ash</A> and <A href ="https://x.com/DropSiteNews/status/2030670499517239725?s=20">oil</A> fall down
          again the morning after; I watch apartment complexes{" "}
          <A href="https://x.com/sahouraxo/status/2029989641038725528?s=20">
            get detonated
          </A>{" "}
          in Lebanon; I watch settlers{" "}
          <A href="https://x.com/Parodyjeffx/status/2031617082287153187?s=20">
            pour cement over wells
          </A>{" "}
          used by Palestinians; I watch kittens belonging to a Palestinian family{" "}
          <A href="https://x.com/MintPressNews/status/2031816485350375452?s=20">
            thrown off roofs
          </A>
          .
        </p>

        <p className="mb-4">
          I see{" "}
          <A href="https://x.com/AJEnglish/status/2030853504974114880?s=20">
            drains on fire
          </A>; I see{" "}
          <A href="https://www.instagram.com/p/C8T9R0asxjy/?hl=en">
            a map of the Greater Israel project
          </A>{" "}
          on the shirtsleeves of soldiers, which encompasses land in Lebanon, Syria,
          Jordan, Egypt, Iraq, Saudi Arabia, and Yemen; I see  <A href="https://en.idi.org.il/articles/59726#:~:text=To%20what%20extent%20should%20Israel%20take%20into%20account%20the%20suffering%20of%20the%20Iranian%20civilian%20population%20when%20planning%20military%20operations%20there%3F%20(%25)">survey results</A> that say the
          majority of Israeli Jews do not care about civilian casualties in Iran; I see that a five-year-old girl and seven-year-old boy{" "}
          <A href="https://www.aljazeera.com/news/liveblog/2026/3/5/iran-live-us-senate-backs-trumps-attacks-on-tehran-israel-pounds-lebanon?update=4369907">
            were killed in a raid
          </A>{" "}
          in the south of Lebanon; I see that Israel{" "}
          <A href="https://x.com/falasteen47/status/2031877207199474167?s=20">
            bombed tents of displaced people
          </A>{" "}
          at the beach in Beirut.
        </p>

        <p className="mb-4">
          I listen to a little girl{" "}
          <A href="https://x.com/TurkishArc/status/2030645008659886293?s=20">
            scream that she wants her mom
          </A>
          , who had already been killed; I listen to Matt Schlap, the chairman of the
          American Conservative Union, say that the schoolgirls killed in Iran are{" "}
          <A href="https://www.youtube.com/watch?v=Ug-msEdw7vk">
            “better dead than in a burqa”
          </A>
          ; I listen to Trump say{" "}
          <A href="https://x.com/OunkaOnX/status/2030440338100469907?s=20">
            &quot;Iranians are among the most evil people ever, they cut babies&apos;
            heads off, chop women in half.&quot;
          </A>

        </p>

        <p className="mb-4">
          I read a story about a woman who was <A href="https://zeteo.com/p/leqaa-kordia-ice-detention-women-trump-administration">
            placed in ICE detention for a year
          </A>{" "} because she called for an end to a genocide which killed 200 members of her family; I read a story about men who were{" "}
          <A href="https://www.the-independent.com/news/world/middle-east/israeli-sniper-gaza-brothers-al-aweini-b2927979.html">
            shot while crawling to the hospital
          </A>
          ; I read that Congressman Andy Ogles wrote{" "}
          <A href="https://x.com/RepOgles/status/2031449464825667735?s=20">
            “Muslims are unable to assimilate; they all have to go back”
          </A>
          ; I read about how American companies{" "}
          <A href="https://www.aljazeera.com/opinions/2025/3/5/why-is-america-afraid-of-no-other">
            did not pick up <em>No Other Land</em>
          </A>{" "}
          despite it winning an Oscar.
        </p>

      <p className="mb-4">
          I remember the reasons:{" "}
          <A href="https://x.com/mehdirhasan/status/2029544008926568900?s=20">
            “if we didn&apos;t hit within two weeks, they
            would&apos;ve had a nuclear weapon”
          </A>
          ;{" "}
          <A href="https://www.haaretz.com/us-news/2023-10-25/ty-article/.premium/new-house-speaker-mike-johnson-an-evangelical-christian-holds-ties-to-israels-far-right/0000018b-67b3-db57-a7cb-efbf8a8d0000">
            “we must fulfill the prophecy”
          </A>
          ;{" "}
          <A href="https://www.nytimes.com/2019/03/30/us/politics/pompeo-christian-policy.html#:~:text=%E2%80%9CAs%20a%20Christian,at%20work%20here.%E2%80%9D">
            “I am confident that the Lord is at work here.”
          </A>
        </p>

        <p className="mb-4">
          I remember the confessions: a group of soldiers admitted to being offered{" "}
          <A href="https://www.aa.com.tr/en/middle-east/former-israeli-soldiers-confess-to-oppressing-palestinians-for-years/3099887">
            “pizza coupons”
          </A>{" "} for shooting Palestinians; another said,{" "}
          <A href="https://www.theguardian.com/world/2025/nov/10/israeli-soldiers-breaking-ranks-gaza-civilians-human-shields">
            “if you want to shoot without restraint, you can.”
          </A>{" "} Some soldiers{" "}
          <A href="https://jewishcurrents.org/what-the-soldiers-did-in-gaza">
            raped hostages
          </A>{" "} while others{" "}
          <A href="https://english.elpais.com/international/2025-08-21/israeli-soldier-recalls-how-gazan-civilians-were-used-as-human-shields-something-clicked-in-my-head-forever.html#">
            used young Palestinians to detect booby traps
          </A>
          .
        </p>

      
        <p className="mb-4">
          I remember <A href ="https://www.youtube.com/watch?v=7JgDj0YLMdI">the BBC cut a broadcast</A> when a reporter said Israel was using white phosphorus in Gaza, which Human Rights Watch has confirmed <A href="https://www.hrw.org/news/2023/10/12/israel-white-phosphorus-used-gaza-lebanon">again</A>, <A href ="https://www.hrw.org/news/2024/06/05/lebanon-israels-white-phosphorous-use-risks-civilian-harm">again</A>, and <A href="https://www.hrw.org/news/2026/03/09/lebanon-israel-unlawfully-using-white-phosphorus">again</A>, with first reports dating back to <A href="https://imeu.org/resources/gaza/quick-facts-israels-illegal-use-of-white-phosphorus/461">2008</A>. 
          </p>

                  {/* <hr className="my-6" /> */}

        <p className="mb-4">
        “Eliminate everything that's in Dahiya, Baalbek, Tyre, Sidon, Nabatieh &mdash; everywhere”, <A href = "https://x.com/_ZachFoster/status/2032202750189007248?s=20">said Yoav Gallant</A>, former Minister of Defense of Israel. Destruction is the goal, isn't that obvious? There are holes in the ground and dead bodies in them, from <A href = "https://x.com/sahouraxo/status/2032117355086508537?s=20">Lebanese University</A> to the <A href ="https://x.com/ShaykhSulaiman/status/2032032451552321765?s=20">Roman temples</A>. I watch the same operations in cycles: Israel just <A href="https://www.dropsitenews.com/p/lebanon-hezbollah-army-israel-war-displacement-litani-river-beirut">ordered all civilians to evacuate a zone of 25 miles from the Israeli border</A>, totaling 10% of Lebanon and home to one million people. June Jordan continues her 2005 poem, “Didn’t you read the leaflets that they dropped / from their hotshot fighter jets? / They told you to go.”</p> 

        <p className="mb-4">
        I spiral over a cup of coffee in the Stanford sunlight. I get a notification on my phone, <em>Justin, you have to see this</em>, it's <A href ="https://www.instagram.com/p/DV0UHL0DhzG/?utm_source=ig_web_copy_link&igsh=NTc4MTIwNjQ2YQ==">gunfire on crowds of civilians in Gaza</A>. My skin tans; my friends are joining me in a few minutes; I just read that 15 medical workers were killed in one attack by Israel. Negligence is a crowded room. Please leave it with me. There is something on the other side of our sorrow.</p>



        {/* <p className="mb-4">
            Within me there is a distance between my mind and my body which yearns to be bridged. I order a coffee, while I drink, thousands of people are displaced from their homes; my coffee stays hot. In the evening, we sing karaoke in vicinity of cigarettes; the woman named America plays <em>Bella Ciao</em>; a refugee camp catches fire and smoke becomes ash becomes rain becomes drinking water. I would like to say I live a layered life. The truth is that I live a fractured life; my joy is my guilt. As Khalil Gibran writes: "Pleasure is a freedom-song / But it is not freedom / It is the blossoming of your desires, / But it is not their fruit." One day, I want to smile again without caveat.
            </p>
          
        <p className="mb-4">
            We cannot be blissfully ignorant of the true horror within the world. It is precisely this suspension of belief which enables these horrors to continue. Please leave the bubble. Wake the fuck up. We have to.
            </p> */}

        </div>
      </div>
    </FootnoteProvider>
  );
}

export default withBlogSEO(Leave, {
  title: "please leave",
  type: "blog",
});
